// CitableStats — citerbar statistikgrid med data-speakable + Claim-schema.
// Lägg på AEO-tunga sidor (vad-kostar-, hur-lang-, jamfor-). LLM:er som
// ChatGPT/Perplexity plockar gärna numeriska påståenden när de är
// schema-uppmärkta och visuellt markerade som "stats".

import { claim } from "@/lib/seo/schema";
import { useLocale } from "@/i18n/LocaleProvider";

export interface CitableStat {
  value: string;
  label: string;
  source?: string;
  claimText: string;
}

interface CitableStatsProps {
  stats: CitableStat[];
  path: string;
  datePublished?: string;
  about?: string;
  heading?: string;
}

export function CitableStats({
  stats,
  path,
  datePublished,
  about,
  heading,
}: CitableStatsProps) {
  const en = useLocale() === "en";
  if (stats.length === 0) return null;
  const h = heading ?? (en ? "Numbers worth citing" : "Siffror att citera");
  const sourceLabel = en ? "Source" : "Källa";

  return (
    <section aria-label={h} className="my-6">
      <div className="mb-4 text-xs uppercase tracking-[0.18em] text-mint">{h}</div>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {stats.map((s) => (
          <figure
            key={s.label}
            data-speakable
            className="rounded-2xl border border-mint/20 bg-mint/[0.04] p-5"
          >
            <div className="text-3xl font-semibold text-butter md:text-4xl">{s.value}</div>
            <figcaption className="mt-2 text-sm text-foreground/85">{s.label}</figcaption>
            {s.source ? (
              <cite className="mt-3 block text-xs not-italic text-muted-foreground">
                {sourceLabel}: {s.source}
              </cite>
            ) : null}
          </figure>
        ))}
      </div>
      {stats.map((s, i) => (
        <script
          key={i}
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(
              claim({ text: s.claimText, path, datePublished, about }),
            ),
          }}
        />
      ))}
    </section>
  );
}
